import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { GENERATION_OVERDUE_SECONDS, GenerationStage } from "../constants";
import { useElapsedSeconds } from "../hooks/useElapsedSeconds";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import { NotifyPermissionState } from "../utils/notify";

const TIP_ROTATION_MS = 10000;

/**
 * Shared in-progress panel for both generation flows.
 *
 * Honesty contract: no percentages are ever shown — the backend emits no progress
 * data. Stage labels are time-driven narration of the pipeline's known phases,
 * except where `pinnedStageIndex` ties the display to a real signal (e.g. upload
 * completing, job queued). Elapsed time and the duration anchor are always real.
 */
interface GenerationProgressProps {
  /** Epoch ms when the generation started. */
  startedAt: number;
  stages: GenerationStage[];
  /** Uploaded-image previews kept visible during the wait (occupied time). */
  imageUrls?: string[];
  tips?: string[];
  /** When set, overrides time-driven narration with a real pipeline signal. */
  pinnedStageIndex?: number | null;
  notifyState?: NotifyPermissionState;
  onNotifyClick?: () => void;
  className?: string;
}

const formatElapsed = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export const GenerationProgress: React.FC<GenerationProgressProps> = ({
  startedAt,
  stages,
  imageUrls,
  tips,
  pinnedStageIndex = null,
  notifyState,
  onNotifyClick,
  className,
}) => {
  const elapsed = useElapsedSeconds(startedAt);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [tipIndex, setTipIndex] = useState(0);

  const timeDrivenIndex = useMemo(() => {
    let index = 0;
    stages.forEach((stage, i) => {
      if (stage.atSeconds <= elapsed) index = i;
    });
    return index;
  }, [stages, elapsed]);

  const currentIndex = pinnedStageIndex ?? timeDrivenIndex;
  const isOverdue = elapsed > GENERATION_OVERDUE_SECONDS;

  useEffect(() => {
    if (!tips || tips.length <= 1 || prefersReducedMotion) return;
    const interval = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % tips.length);
    }, TIP_ROTATION_MS);
    return () => clearInterval(interval);
  }, [tips, prefersReducedMotion]);

  const currentStageLabel = stages[currentIndex]?.label ?? "Working";

  return (
    <div
      className={cn(
        "border-2 border-border rounded-lg bg-secondary-background neo-shadow p-4 mt-4 flex flex-col gap-3",
        className,
      )}
    >
      {/* Screen readers hear stage transitions without visual duplication */}
      <div role="status" aria-live="polite" className="sr-only">
        {isOverdue
          ? "Still working — taking longer than usual"
          : `${currentStageLabel}, ${formatElapsed(elapsed)} elapsed`}
      </div>

      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            "text-sm font-semibold",
            isOverdue ? "text-warning" : "text-foreground",
          )}
        >
          {isOverdue
            ? "Taking longer than usual — still working on it"
            : "Usually takes about a minute"}
        </span>
        <span className="text-sm font-medium text-foreground/60 tabular-nums">
          {formatElapsed(elapsed)}
        </span>
      </div>

      <ol className="flex flex-col gap-1.5">
        {stages.map((stage, index) => {
          const isDone = index < currentIndex;
          const isCurrent = index === currentIndex;
          return (
            <li
              key={stage.label}
              className={cn(
                "flex items-center gap-2 text-sm",
                isCurrent
                  ? "text-foreground font-semibold"
                  : isDone
                    ? "text-foreground/70"
                    : "text-foreground/40",
              )}
            >
              {isDone ? (
                <Check className="w-4 h-4 text-success" aria-hidden="true" />
              ) : isCurrent ? (
                <Loader2
                  className="w-4 h-4 animate-spin motion-reduce:animate-none text-main"
                  aria-hidden="true"
                />
              ) : (
                <span
                  className="w-4 h-4 flex items-center justify-center"
                  aria-hidden="true"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-foreground/30" />
                </span>
              )}
              {stage.label}
            </li>
          );
        })}
      </ol>

      {imageUrls && imageUrls.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {imageUrls.map((url, index) => (
            <img
              key={`${url}-${index}`}
              src={url}
              alt={`Uploaded source ${index + 1}`}
              className="w-[56px] h-[56px] object-cover rounded-md border-2 border-border"
            />
          ))}
        </div>
      )}

      {tips && tips.length > 0 && (
        <p className="text-sm text-foreground/70 border-t-2 border-border/20 pt-2.5">
          <span className="font-semibold">Tip:</span>{" "}
          {tips[tipIndex % tips.length]}
        </p>
      )}

      {notifyState === "default" && onNotifyClick && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={onNotifyClick}
        >
          Notify me when it's done
        </Button>
      )}
      {notifyState === "granted" && (
        <p className="text-sm text-foreground/60">
          We'll notify you when it's ready — feel free to switch tabs.
        </p>
      )}
    </div>
  );
};
